import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { ChevronDown, ChevronUp, GripVertical, Plus } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { usePreferences } from '@/shell/stores/preferences'

/**
 * Stable cell-key format used for `dirtyCells` / `cellErrors` external
 * state. Exported so callers (e.g. Sprint 01b's PricelistEditor) format
 * keys identically to the grid's internals, regardless of column key
 * name or ordering.
 *
 * Keys are written against the SOURCE row index (the position of the row
 * in `props.rows`). This stays stable across sort / drag-reorder of the
 * displayed rows — the caller can populate the maps once and they keep
 * referring to the same underlying record.
 */
// eslint-disable-next-line react-refresh/only-export-components -- cellKey lives next to the grid by design (Sprint 01b contract); consumers use the same module to align key format.
export const cellKey = (rowIdx: number, colIdx: number): string =>
  `${rowIdx}:${colIdx}`

export interface EditableColumnDef<T> {
  /** Accessor key into the row. */
  key: keyof T
  header: ReactNode
  /**
   * Read-only cell — mouse click won't enter edit mode.
   * - Omit or `false`: column is editable.
   * - `true`: every row is locked for this column.
   * - Function `(row) => boolean`: row-level lock. Used by Pricelists'
   *   Product column to lock existing rows (the product identity is
   *   immutable post-creation) while still allowing draft rows to set
   *   their product via the renderEditor on first commit.
   */
  readonly?: boolean | ((row: T) => boolean)
  /** Right-align + monospace for numeric columns. */
  numeric?: boolean
  /** Fixed column width (CSS length). */
  width?: string
  /** Render for display (non-editing state). Defaults to `String(value)`. */
  render?: (value: T[keyof T], row: T) => ReactNode
  /**
   * Custom editor component for this column. When the cell enters edit
   * mode, the grid renders `renderEditor(...)` instead of the default
   * text `<input>`. The custom editor owns its own keyboard handling;
   * it calls `onCommit(value)` to commit (validators still run) or
   * `onCancel()` to revert. `value` arrives already in its final type
   * — no string coercion — so the editor passes through whatever shape
   * the row column uses (number, Odoo Many2one tuple, ISO date, etc.).
   *
   * Used by: Sprint 01b's PricelistEditor for product / manufacturer /
   * country / date columns. Formulations (Sprint 04) + Quoting (Sprint
   * 07) will consume the same hook.
   */
  renderEditor?: (args: {
    value: T[keyof T]
    row: T
    rowIndex: number
    onCommit: (next: unknown) => void
    onCancel: () => void
  }) => ReactNode
  /**
   * Sort behaviour when `enableSorting` is on at the grid level.
   * - Omitted / `true` (default): column participates with the built-in
   *   comparator (string-coerce + locale-aware compare; nulls sort last).
   * - `false`: header click does nothing; column is excluded from sort.
   * - Function: custom comparator on the row, applied in ascending
   *   direction; descending flips the result.
   */
  sortable?: boolean
  sortFn?: (a: T, b: T) => number
}

export type EditableGridValidator<T> = (
  value: unknown,
  row: T,
  rowIndex: number,
) => string | null

export interface EditableGridProps<T> {
  rows: T[]
  columns: EditableColumnDef<T>[]
  /** Callback when a cell commits (Enter / blur). `rowIndex` is the
   *  source index — i.e. the position in `props.rows` — even when the
   *  grid is rendering a sorted display order. */
  onCellChange: (rowIndex: number, key: keyof T, value: unknown) => void
  /** Per-column validators keyed by column key. Return null on valid. */
  validators?: Partial<Record<keyof T, EditableGridValidator<T>>>
  /** Fires when ⌘S / Ctrl+S is pressed. Component does NOT auto-clear dirty. */
  onSave?: () => void | Promise<void>
  /** Add a row at the bottom. */
  onAddRow?: () => void
  addRowLabel?: string
  /** Reorder rows via drag on the row handle. Disabled automatically
   *  whenever an internal sort is active — drag-to-reorder would have
   *  ambiguous semantics against a sorted view. */
  onRowReorder?: (fromIndex: number, toIndex: number) => void
  /** Bottom totals — takes the current rows and returns a render map per column. */
  totalsRow?: (rows: T[]) => Partial<Record<keyof T, { value: ReactNode; tone?: 'ok' | 'bad' }>>
  /** Keys considered dirty — external state wins so the caller controls persistence.
   *  Keys must use SOURCE row indices (the position in `props.rows`). */
  dirtyCells?: ReadonlySet<string>
  /**
   * External per-cell errors, keyed by `cellKey(rowIdx, colIdx)`. When
   * the caller routes a server-side validation failure (e.g. Odoo 400
   * on a duplicate `customer_part_number`), setting an entry here
   * lights up the cell's danger-border + inline error bubble on the
   * offending cell — no toast fallback needed.
   *
   * Keys use SOURCE row indices (the position in `props.rows`), so the
   * map survives sort / drag-reorder of the display order.
   *
   * Merge rule: external `cellErrors` wins over the grid's internal
   * validator-driven error state for the same key. Consistent with
   * `dirtyCells` — callers own the canonical state.
   */
  cellErrors?: ReadonlyMap<string, string>
  /** Title rendered in the header toolbar. */
  title?: ReactNode
  /** Right-aligned metadata in the header toolbar (e.g. "12 lines · 3 dirty"). */
  meta?: ReactNode
  /** Keyboard hint banner copy. */
  hint?: ReactNode
  /**
   * CSS height ceiling applied to the grid's root. The scrollable row
   * region below the sticky header + toolbar grows up to this cap, then
   * overflows vertically. Defaults to `'70vh'`.
   */
  maxHeight?: string
  /**
   * Minimum horizontal width the `<table>` insists on. Default `'900px'`.
   */
  minTableWidth?: string
  /**
   * Optional trailing-column slot — narrow column at the right edge per
   * row. Non-interactive at the grid level; inner controls own their
   * own click handling.
   */
  rowActions?: (row: T, rowIndex: number) => ReactNode
  /**
   * Per-user column-state persistence key. When provided, drag-reorder
   * of columns persists to `usePreferences.tableColumns[moduleId].order`
   * (same slot DataTable consumers use since Sprint 17 — no migration).
   * Enabling `moduleId` also opts the grid into the same default-on
   * behaviour for column reorder + click-to-sort headers.
   */
  moduleId?: string
  /** Drag-to-reorder column headers. Defaults to `!!moduleId`. */
  enableColumnReorder?: boolean
  /** Click-to-sort column headers. Defaults to `!!moduleId`. */
  enableSorting?: boolean
  className?: string
}

type FocusCell = { row: number; col: number } | null
type SortDir = 'asc' | 'desc'
type SortState<T> = { key: keyof T; dir: SortDir } | null

const _columnKeyToId = (k: PropertyKey): string => String(k)

/**
 * Evaluate a column's `readonly` against a specific row. Supports the
 * static-boolean form (every row) and the row-aware function form (lets
 * a column lock some rows but not others — used for Pricelists' Product
 * column to keep existing items immutable while allowing draft rows to
 * set their product on first commit).
 */
function _isReadonly<T>(
  col: EditableColumnDef<T>,
  row: T | undefined,
): boolean {
  if (typeof col.readonly === 'function') {
    return row === undefined ? false : col.readonly(row)
  }
  return Boolean(col.readonly)
}

function _defaultCompare<T>(
  a: T,
  b: T,
  key: keyof T,
): number {
  const av = a[key]
  const bv = b[key]
  // Nulls sort last, regardless of direction (direction flip happens after).
  const aNullish = av === null || av === undefined || av === ''
  const bNullish = bv === null || bv === undefined || bv === ''
  if (aNullish && bNullish) return 0
  if (aNullish) return 1
  if (bNullish) return -1
  if (typeof av === 'number' && typeof bv === 'number') {
    return av - bv
  }
  return String(av).localeCompare(String(bv), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

/**
 * Keyboard-driven editable grid. Highest-frequency interactive surface in
 * the product — Formulations, Quoting, Pricelists all compose this.
 *
 * Keyboard contract:
 * - Tab / Shift+Tab: move focus across cells (wraps to next / previous row)
 * - Enter: commit + move down
 * - Esc: revert + exit edit mode
 * - ↑ / ↓: move focus between rows (no edit)
 * - ⌘S / Ctrl+S: fires `onSave`
 *
 * Validation: per-column validator returns a string error → cell paints
 * danger border and shows an inline bubble below. No commit until valid.
 *
 * Sort + reorder (Sprint 18+): opt in by passing `moduleId`. Column
 * reorder persists per-user; sort state is transient (in-memory).
 */
export function EditableGrid<T>({
  rows,
  columns,
  onCellChange,
  validators,
  onSave,
  onAddRow,
  addRowLabel = 'Add row',
  onRowReorder,
  totalsRow,
  dirtyCells,
  cellErrors,
  title,
  meta,
  hint = 'Tab | Enter | Esc | ↑↓ | ⌘S',
  maxHeight = '70vh',
  minTableWidth = '900px',
  rowActions,
  moduleId,
  enableColumnReorder,
  enableSorting,
  className,
}: EditableGridProps<T>) {
  const reorderEnabled = enableColumnReorder ?? Boolean(moduleId)
  const sortingEnabled = enableSorting ?? Boolean(moduleId)

  const [focus, setFocus] = useState<FocusCell>(null)
  const [editing, setEditing] = useState<FocusCell>(null)
  const [draft, setDraft] = useState<string>('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [sort, setSort] = useState<SortState<T>>(null)
  const dragRowRef = useRef<number | null>(null)

  // ─── Column order ────────────────────────────────────────────────────
  const persistedColumnOrder = usePreferences((s) =>
    moduleId ? s.tableColumns[moduleId]?.order : undefined,
  )
  const setTableColumnOrder = usePreferences((s) => s.setTableColumnOrder)

  const orderedColumns = useMemo<EditableColumnDef<T>[]>(() => {
    if (!reorderEnabled || !persistedColumnOrder || persistedColumnOrder.length === 0) {
      return columns
    }
    const byId = new Map<string, EditableColumnDef<T>>()
    columns.forEach((c) => byId.set(_columnKeyToId(c.key), c))
    const orderIndexById = new Map<string, number>()
    persistedColumnOrder.forEach((id, i) => orderIndexById.set(id, i))
    return [...columns].sort((a, b) => {
      const ai = orderIndexById.get(_columnKeyToId(a.key)) ?? Number.POSITIVE_INFINITY
      const bi = orderIndexById.get(_columnKeyToId(b.key)) ?? Number.POSITIVE_INFINITY
      if (ai !== bi) return ai - bi
      // Ties (both unordered): preserve authored position.
      return columns.indexOf(a) - columns.indexOf(b)
    })
  }, [columns, persistedColumnOrder, reorderEnabled])

  const orderedColumnIds = useMemo(
    () => orderedColumns.map((c) => _columnKeyToId(c.key)),
    [orderedColumns],
  )

  // ─── Sort ────────────────────────────────────────────────────────────
  // displayToSource[displayIdx] → source index in `rows`. Used at every
  // internal-display ↔ outward-callback boundary so caller indices stay
  // stable across sort changes.
  const displayToSource = useMemo<number[]>(() => {
    const indices = rows.map((_, i) => i)
    if (sort) {
      const col = columns.find((c) => c.key === sort.key)
      const cmp = col?.sortFn ?? ((a: T, b: T) => _defaultCompare(a, b, sort.key))
      indices.sort((i, j) => {
        const r = cmp(rows[i]!, rows[j]!)
        return sort.dir === 'asc' ? r : -r
      })
    }
    return indices
  }, [rows, columns, sort])

  // Sort + column-order changes happen via cycleSort + onColumnDragEnd
  // below. Both handlers clear focus + editing at the point of the
  // change — a useEffect watching sort/orderedColumnIds would call
  // setState during render, which the lint rule react-hooks/set-state-
  // in-effect rightly flags. Clearing at the call site avoids the
  // cascading-render path entirely.

  // Convenience: caller's source-indexed dirty/error maps lookup by source.
  const lookupDirty = useCallback(
    (sourceIdx: number, colIdx: number): boolean => {
      if (!dirtyCells) return false
      return dirtyCells.has(cellKey(sourceIdx, colIdx))
    },
    [dirtyCells],
  )
  const lookupCellError = useCallback(
    (sourceIdx: number, colIdx: number): string | undefined => {
      const k = cellKey(sourceIdx, colIdx)
      return cellErrors?.get(k) ?? errors[k]
    },
    [cellErrors, errors],
  )

  // ─── Sort header click ───────────────────────────────────────────────
  const isSortableColumn = useCallback(
    (col: EditableColumnDef<T>) => {
      if (!sortingEnabled) return false
      // Default sortable=true when sorting enabled; explicit false opts out.
      return col.sortable !== false
    },
    [sortingEnabled],
  )

  const cycleSort = useCallback((col: EditableColumnDef<T>) => {
    if (!isSortableColumn(col)) return
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: 'asc' }
      if (prev.dir === 'asc') return { key: col.key, dir: 'desc' }
      return null
    })
    // Re-sorting reshuffles which source row sits at each display index,
    // so the current focus/editing pointers are stale. Drop them.
    setEditing(null)
    setFocus(null)
  }, [isSortableColumn])

  const enterEdit = useCallback(
    (displayRow: number, displayCol: number) => {
      const col = orderedColumns[displayCol]
      if (!col) return
      const sourceRow = displayToSource[displayRow]
      if (sourceRow === undefined) return
      const row = rows[sourceRow]
      if (_isReadonly(col, row)) return
      setFocus({ row: displayRow, col: displayCol })
      setEditing({ row: displayRow, col: displayCol })
      const raw = row?.[col.key]
      setDraft(raw === undefined || raw === null ? '' : String(raw))
    },
    [orderedColumns, rows, displayToSource],
  )

  /**
   * Run validator → on success call `onCellChange`, clear internal error
   * for this cell, return true; on failure populate internal error and
   * return false. Indices in / out are SOURCE indices.
   */
  const commitTyped = useCallback(
    (sourceRow: number, displayCol: number, coerced: unknown): boolean => {
      const col = orderedColumns[displayCol]
      if (!col) return false
      const row = rows[sourceRow]
      if (!row) return false
      const validator = validators?.[col.key]
      const err = validator ? validator(coerced, row, sourceRow) : null
      const k = cellKey(sourceRow, displayCol)
      if (err) {
        setErrors((prev) => ({ ...prev, [k]: err }))
        return false
      }
      setErrors((prev) => {
        const next = { ...prev }
        delete next[k]
        return next
      })
      onCellChange(sourceRow, col.key, coerced)
      return true
    },
    [orderedColumns, rows, validators, onCellChange],
  )

  const commit = useCallback(
    (displayRow: number, displayCol: number, value: string): boolean => {
      const col = orderedColumns[displayCol]
      if (!col) return false
      const sourceRow = displayToSource[displayRow]
      if (sourceRow === undefined) return false
      const coerced: unknown = col.numeric ? (value === '' ? null : Number(value)) : value
      return commitTyped(sourceRow, displayCol, coerced)
    },
    [orderedColumns, commitTyped, displayToSource],
  )

  const cancelEdit = () => {
    setEditing(null)
  }

  const moveFocus = (dRow: number, dCol: number) => {
    setFocus((prev) => {
      if (!prev) return prev
      const nextRow = Math.max(0, Math.min(rows.length - 1, prev.row + dRow))
      let nextCol = prev.col + dCol
      let targetRow = nextRow
      if (nextCol < 0) {
        if (targetRow === 0) return prev
        targetRow -= 1
        nextCol = orderedColumns.length - 1
      } else if (nextCol >= orderedColumns.length) {
        if (targetRow === rows.length - 1) return prev
        targetRow += 1
        nextCol = 0
      }
      return { row: targetRow, col: nextCol }
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const isMeta = e.metaKey || e.ctrlKey
    if (isMeta && e.key.toLowerCase() === 's') {
      e.preventDefault()
      onSave?.()
      return
    }
    if (editing) {
      // Editing-specific keybinds handled on the <input> itself.
      return
    }
    if (!focus) return

    switch (e.key) {
      case 'Enter':
        e.preventDefault()
        enterEdit(focus.row, focus.col)
        break
      case 'Escape':
        setFocus(null)
        break
      case 'ArrowUp':
        e.preventDefault()
        moveFocus(-1, 0)
        break
      case 'ArrowDown':
        e.preventDefault()
        moveFocus(1, 0)
        break
      case 'Tab':
        e.preventDefault()
        moveFocus(0, e.shiftKey ? -1 : 1)
        break
    }
  }

  const handleCellKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const displayRow = editing!.row
    const displayCol = editing!.col
    if (e.key === 'Enter') {
      e.preventDefault()
      if (commit(displayRow, displayCol, draft)) {
        setEditing(null)
        setFocus({ row: Math.min(rows.length - 1, displayRow + 1), col: displayCol })
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    } else if (e.key === 'Tab') {
      e.preventDefault()
      if (!commit(displayRow, displayCol, draft)) return
      setEditing(null)
      const dir = e.shiftKey ? -1 : 1
      const nextCol = displayCol + dir
      if (nextCol >= 0 && nextCol < orderedColumns.length) {
        setFocus({ row: displayRow, col: nextCol })
      } else if (dir > 0 && displayRow < rows.length - 1) {
        setFocus({ row: displayRow + 1, col: 0 })
      } else if (dir < 0 && displayRow > 0) {
        setFocus({ row: displayRow - 1, col: orderedColumns.length - 1 })
      }
    }
  }

  const totals = totalsRow?.(rows)

  // Drag-to-reorder is disabled when a sort is active (ambiguous semantics).
  const rowReorderActive = onRowReorder && !sort

  // ─── @dnd-kit setup for column reorder ───────────────────────────────
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  const onColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!moduleId || !over || active.id === over.id) return
      const ids = orderedColumnIds
      const oldIndex = ids.indexOf(String(active.id))
      const newIndex = ids.indexOf(String(over.id))
      if (oldIndex < 0 || newIndex < 0) return
      const next = [...ids]
      const [moved] = next.splice(oldIndex, 1)
      next.splice(newIndex, 0, moved!)
      setTableColumnOrder(moduleId, next)
      // The current focus/editing pointers reference colIdx in the old
      // order — they no longer point to the same column. Drop them.
      setEditing(null)
      setFocus(null)
    },
    [moduleId, orderedColumnIds, setTableColumnOrder],
  )

  const gridMarkup = (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-md border border-border-default bg-surface',
        className,
      )}
      style={{ maxHeight }}
      tabIndex={0}
      role="region"
      aria-label={typeof title === 'string' ? title : 'Editable grid'}
      onKeyDown={handleKeyDown}
    >
      {(title || meta) && (
        <header className="flex flex-shrink-0 items-center gap-2 border-b border-border-subtle bg-surface-sunk px-3 py-2 text-sm">
          {title ? <span className="flex-1 font-semibold text-primary">{title}</span> : <span className="flex-1" />}
          {meta ? <span className="text-role-metadata">{meta}</span> : null}
        </header>
      )}
      {/* Scroll region. The table's `min-width` triggers horizontal
          overflow; `min-h-0 flex-1` + the root's maxHeight give it a
          bounded height so vertical overflow engages. */}
      <div
        className="min-h-0 flex-1 overflow-auto"
        data-slot="editable-grid-scroll"
      >
      <table
        className="w-full table-fixed border-separate border-spacing-0 text-sm"
        style={{ minWidth: minTableWidth }}
      >
        <thead>
          <tr>
            {rowReorderActive ? (
              <th className="sticky top-0 z-[2] w-6 bg-surface-sunk" />
            ) : null}
            <th className="sticky top-0 z-[2] w-9 bg-surface-sunk py-0 text-center text-2xs text-tertiary tabular-nums">#</th>
            {orderedColumns.map((col) => (
              <SortableHeader
                key={String(col.key)}
                col={col}
                reorderEnabled={reorderEnabled}
                sortable={isSortableColumn(col)}
                sortDir={sort && sort.key === col.key ? sort.dir : null}
                onSortClick={() => cycleSort(col)}
              />
            ))}
            {rowActions ? (
              <th
                aria-label="Row actions"
                className="sticky top-0 z-[2] w-10 border-b border-border-default bg-surface-sunk"
              />
            ) : null}
          </tr>
        </thead>
        <tbody>
          {displayToSource.map((sourceRow, displayRow) => {
            const row = rows[sourceRow]!
            const isFocusedRow = focus?.row === displayRow
            return (
              <tr
                key={sourceRow}
                data-focused={isFocusedRow || undefined}
                onDragOver={rowReorderActive ? (e) => e.preventDefault() : undefined}
                onDrop={
                  rowReorderActive
                    ? () => {
                        if (
                          dragRowRef.current !== null &&
                          dragRowRef.current !== sourceRow
                        ) {
                          onRowReorder!(dragRowRef.current, sourceRow)
                        }
                        dragRowRef.current = null
                      }
                    : undefined
                }
                className={cn(isFocusedRow && 'bg-accent-muted')}
              >
                {rowReorderActive ? (
                  <td
                    draggable
                    onDragStart={() => (dragRowRef.current = sourceRow)}
                    aria-label="Drag to reorder"
                    className="w-6 cursor-grab select-none border-b border-border-subtle border-r border-r-border-subtle bg-surface-sunk text-center text-tertiary hover:text-primary active:cursor-grabbing"
                  >
                    <GripVertical size={12} />
                  </td>
                ) : null}
                <td className="w-9 border-b border-border-subtle border-r border-r-border-subtle bg-surface-sunk text-center text-2xs text-tertiary tabular-nums">
                  {displayRow + 1}
                </td>
                {orderedColumns.map((col, displayCol) => {
                  const cellK = cellKey(sourceRow, displayCol)
                  const isEditing =
                    editing?.row === displayRow && editing?.col === displayCol
                  const isFocused =
                    focus?.row === displayRow &&
                    focus?.col === displayCol &&
                    !isEditing
                  const raw = row[col.key]
                  const display =
                    col.render
                      ? col.render(raw, row)
                      : raw === undefined || raw === null
                        ? ''
                        : String(raw)
                  // External cellErrors win over internal validator errors
                  // for the same cell (consistent with dirtyCells semantics).
                  const errorMsg = lookupCellError(sourceRow, displayCol)
                  const isDirty = lookupDirty(sourceRow, displayCol)
                  const cellReadonly = _isReadonly(col, row)
                  return (
                    <td
                      key={String(col.key)}
                      data-editing={isEditing || undefined}
                      data-focused={isFocused || undefined}
                      data-dirty={isDirty || undefined}
                      data-readonly={cellReadonly || undefined}
                      onClick={() => {
                        setFocus({ row: displayRow, col: displayCol })
                        if (!cellReadonly) enterEdit(displayRow, displayCol)
                      }}
                      className={cn(
                        'relative h-[var(--row-height)] border-b border-border-subtle border-r border-r-border-subtle p-0 align-middle last:border-r-0',
                      )}
                    >
                      {isEditing ? (
                        col.renderEditor ? (
                          <div
                            className="h-full w-full"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                e.stopPropagation()
                              }
                            }}
                          >
                            {col.renderEditor({
                              value: raw,
                              row,
                              rowIndex: sourceRow,
                              onCommit: (next) => {
                                if (commitTyped(sourceRow, displayCol, next)) {
                                  setEditing(null)
                                  setFocus({
                                    row: Math.min(rows.length - 1, displayRow + 1),
                                    col: displayCol,
                                  })
                                }
                              },
                              onCancel: () => setEditing(null),
                            })}
                          </div>
                        ) : (
                          <input
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={() => {
                              if (commit(displayRow, displayCol, draft)) setEditing(null)
                            }}
                            onKeyDown={handleCellKeyDown}
                            className={cn(
                              'h-full w-full border-none bg-transparent px-[var(--cell-padding-x)] text-sm text-primary outline-none',
                              col.numeric && 'text-right tabular-nums',
                            )}
                            aria-invalid={Boolean(errorMsg)}
                            aria-errormessage={errorMsg ? `${cellK}-err` : undefined}
                          />
                        )
                      ) : (
                        <span
                          className={cn(
                            'flex h-full items-center overflow-hidden truncate px-[var(--cell-padding-x)]',
                            col.numeric && 'justify-end tabular-nums',
                            cellReadonly && 'cursor-default bg-surface-sunk text-secondary',
                            errorMsg &&
                              'bg-danger-bg shadow-[inset_0_0_0_2px_var(--color-danger)]',
                          )}
                          data-cell={cellK}
                        >
                          {display}
                        </span>
                      )}
                      {isDirty ? (
                        <span
                          aria-hidden="true"
                          className="absolute left-1 top-1 h-1 w-1 rounded-full bg-accent"
                        />
                      ) : null}
                      {errorMsg ? (
                        <span
                          id={`${cellK}-err`}
                          role="alert"
                          className="absolute left-0 top-full z-drawer whitespace-nowrap rounded-b-xs bg-danger px-2 py-0.5 text-2xs font-medium text-on-accent shadow-sm"
                        >
                          {errorMsg}
                        </span>
                      ) : null}
                    </td>
                  )
                })}
                {rowActions ? (
                  <td
                    className="w-10 border-b border-border-subtle p-0 align-middle"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="flex h-[var(--row-height)] items-center justify-center">
                      {rowActions(row, sourceRow)}
                    </div>
                  </td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
        {totals ? (
          <tfoot className="border-t border-border-default bg-surface-sunk font-semibold">
            <tr>
              {rowReorderActive ? <td className="w-6 border-t border-border-default" /> : null}
              <td className="w-9 border-t border-border-default text-center text-2xs text-tertiary">
                Σ
              </td>
              {orderedColumns.map((col) => {
                const entry = totals[col.key]
                return (
                  <td
                    key={String(col.key)}
                    className={cn(
                      'border-t border-border-default px-[var(--cell-padding-x)] py-[var(--cell-padding-y)] text-sm tabular-nums text-primary',
                      col.numeric && 'text-right',
                      entry?.tone === 'ok' && 'text-success-text',
                      entry?.tone === 'bad' && 'text-danger-text',
                    )}
                  >
                    {entry?.value ?? ''}
                  </td>
                )
              })}
              {rowActions ? (
                <td className="w-10 border-t border-border-default" />
              ) : null}
            </tr>
          </tfoot>
        ) : null}
      </table>
      </div>
      {onAddRow ? (
        <button
          type="button"
          onClick={onAddRow}
          className="flex w-full flex-shrink-0 cursor-pointer items-center gap-1.5 border-none border-t border-t-border-subtle bg-surface px-3 py-2 text-sm font-medium text-accent-text hover:bg-surface-hover"
        >
          <Plus size={14} />
          {addRowLabel}
        </button>
      ) : null}
      {hint ? (
        <footer className="text-role-metadata flex flex-shrink-0 items-center justify-between border-t border-border-subtle bg-surface-sunk px-3 py-1.5">
          <span>{hint}</span>
        </footer>
      ) : null}
    </div>
  )

  if (!reorderEnabled) return gridMarkup

  return (
    <DndContext sensors={dndSensors} onDragEnd={onColumnDragEnd}>
      <SortableContext items={orderedColumnIds} strategy={horizontalListSortingStrategy}>
        {gridMarkup}
      </SortableContext>
    </DndContext>
  )
}

interface SortableHeaderProps<T> {
  col: EditableColumnDef<T>
  reorderEnabled: boolean
  sortable: boolean
  sortDir: SortDir | null
  onSortClick: () => void
}

function SortableHeader<T>({
  col,
  reorderEnabled,
  sortable,
  sortDir,
  onSortClick,
}: SortableHeaderProps<T>) {
  const id = _columnKeyToId(col.key)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: !reorderEnabled })

  const labelText =
    typeof col.header === 'string' ? col.header : String(col.key)

  const sortableStyle: React.CSSProperties = reorderEnabled
    ? {
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        zIndex: isDragging ? 50 : undefined,
        opacity: isDragging ? 0.85 : undefined,
      }
    : {}

  return (
    <th
      ref={reorderEnabled ? setNodeRef : undefined}
      scope="col"
      aria-sort={
        sortDir === 'asc'
          ? 'ascending'
          : sortDir === 'desc'
            ? 'descending'
            : sortable
              ? 'none'
              : undefined
      }
      style={{ ...sortableStyle, ...(col.width ? { width: col.width } : {}) }}
      className={cn(
        'group/th relative sticky top-0 z-[2] h-[var(--header-row-height)] whitespace-nowrap border-b border-border-default border-r border-r-border-subtle',
        'bg-surface-sunk px-[var(--cell-padding-x)] text-left text-2xs font-semibold uppercase tracking-widest text-tertiary last:border-r-0',
        col.numeric && 'text-right',
        // Pad the left side so the grip handle doesn't overlap header text.
        reorderEnabled && 'pl-5',
      )}
    >
      {reorderEnabled ? (
        <span
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${labelText}`}
          data-dragging={isDragging || undefined}
          className={cn(
            'absolute left-0 top-0 z-base inline-flex h-full w-4 cursor-grab items-center justify-center select-none touch-none text-tertiary',
            'opacity-0 group-hover/th:opacity-60 hover:!opacity-100',
            isDragging && 'cursor-grabbing opacity-100',
          )}
        >
          <GripVertical size={10} strokeWidth={2} aria-hidden="true" />
        </span>
      ) : null}
      {sortable ? (
        <button
          type="button"
          onClick={onSortClick}
          aria-label={`Sort by ${labelText}`}
          className={cn(
            'inline-flex items-center gap-1 cursor-pointer border-none bg-transparent p-0',
            'text-2xs font-semibold uppercase tracking-widest text-tertiary',
            'hover:text-primary',
            col.numeric && 'ml-auto',
          )}
        >
          <span>{col.header}</span>
          {sortDir === 'asc' ? (
            <ChevronUp size={10} aria-hidden="true" />
          ) : sortDir === 'desc' ? (
            <ChevronDown size={10} aria-hidden="true" />
          ) : null}
        </button>
      ) : (
        <span>{col.header}</span>
      )}
    </th>
  )
}
